import React, { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { DeploymentChanges } from 'src/components/pipelines/components/deployments-modal';
import { requestDeploymentCommits } from 'src/components/pipelines/redux/actions/deployments';
import { getDeploymentChanges } from 'src/components/pipelines/redux/selectors/deployments';
import { getRepository } from 'src/components/pipelines/redux/selectors/pipelines';
import { useCodeReviewerWorkspaceSetting } from 'src/components/rovo-dev-agents/src/hooks/use-code-reviewer-workspace-setting';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';

import { canDisplayDeploymentSummaryAiAssistant } from './utils';

type Props = {
  isRollback: boolean;
  revisionFrom: string;
  revisionTo: string;
};

const Changes = ({ revisionFrom, revisionTo, isRollback }: Props) => {
  const dispatch = useDispatch();
  const deploymentChanges = useSelector(getDeploymentChanges);
  const repository = useSelector(getRepository);

  const { isEnabledAtWorkspace: workspaceDevAgentsEnabled } =
    useCodeReviewerWorkspaceSetting();

  const deploymentSummaryAiAssistantEnabled = useStatsigGate(
    StatsigFeatureKeys.deploymentSummaryAiAssistantEnabled
  );

  const showMoreCommitsRef = useRef<any>(null);
  useEffect(() => {
    if (
      deploymentChanges.fetchedCommitsPageStatus === LoadingStatus.Success &&
      showMoreCommitsRef.current
    ) {
      showMoreCommitsRef.current.resolve();
      showMoreCommitsRef.current = null;
    }
  }, [deploymentChanges.fetchedCommitsPageStatus]);

  const onShowMoreCommits = useCallback(
    (page: number) => {
      return new Promise(resolve => {
        showMoreCommitsRef.current = { resolve };
        dispatch(
          requestDeploymentCommits(revisionFrom, revisionTo, isRollback, page)
        );
      }) as Promise<void>;
    },
    [dispatch, revisionFrom, revisionTo, isRollback, showMoreCommitsRef]
  );

  return (
    <DeploymentChanges
      repositoryUuid={repository.uuid}
      commits={deploymentChanges.commitDiff}
      diffs={deploymentChanges.fileDiff}
      issues={deploymentChanges.jiraIssues}
      pullRequests={deploymentChanges.pullRequests}
      isJiraConnected={deploymentChanges.isJiraConnected}
      isFetching={deploymentChanges.fetchedStatus !== LoadingStatus.Success}
      isRollback={isRollback}
      onShowMoreCommits={onShowMoreCommits}
      canDisplayDeploymentSummaryAiAssistant={canDisplayDeploymentSummaryAiAssistant(
        workspaceDevAgentsEnabled,
        deploymentSummaryAiAssistantEnabled,
        deploymentChanges
      )}
    />
  );
};

export default React.memo(Changes);
